import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import { Button, CloseButton, Popover, ScrollArea, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useClipboard } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCopy } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { destructEmail } from '~/common/stably-email/destruct-email';
import { emailUsernameFromOrg } from '~/common/stably-email/email-username-from-org';
import { zVariableValidationType } from '~/common/variable/validation';
import { env } from '~/env.mjs';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useTestVariables } from '~/hooks/test/useTestVariables';
import { api } from '~/trpc-extra/api';
import type { ReceiveEmailPart } from '../converters';
import { SENSITIVE_VARIABLE_VALUE } from '~/components/settings/Variables';
import { MailboxQuotaAlert } from '~/components/email/MailboxQuotaAlert';
import { ReceiveEmailSummary } from './ReceiveEmailSummary';
import { ReceiveEmailFields } from './ReceiveEmailFields';
import type { ReceiveEmailFormValues } from './ReceiveEmailFields';
import { useDeepCompareMemo } from '@react-hookz/web';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export default function ReceiveEmailPart({
  testContentId,
  operationId,
  part: { toEmailAddressValue, toEmailAddressType, variableExtraction, filters },
  onClick,
  toggleEditing,
  readonly,
  updateFunc,
  parentContexts,
}: {
  testContentId: string;
  operationId: string;
  part: ReceiveEmailPart;
  onClick?: () => void;
  toggleEditing: (editing: boolean) => void;
  readonly?: boolean;
  updateFunc: (part: Omit<ReceiveEmailPart, 'type'>) => Promise<void>;
  parentContexts?: OperationParentContext[];
}) {
  const projectId = useProjectId();
  const clipboard = useClipboard({ timeout: 500 });
  const testVariables = useTestVariables({
    testContentId,
    limit: { type: 'operationId', id: operationId },
    parentContexts,
  });
  const { data: envVariables } = api.variable.list.useQuery(
    { projectId: projectId! },
    {
      select: (data) =>
        data.map((variable) => ({
          ...variable,
          value: variable.sensitive ? SENSITIVE_VARIABLE_VALUE : variable.value,
        })),
    },
  );
  const [animationParent] = useAutoAnimate();
  const { activeOrg } = useAuthData();
  // Whether or not a dropdown field for inbox is seleceted
  const [isInboxSelected, setIsInboxSelected] = useState(false);
  // Whether the config popover is open — only fetch mailbox quota while it is.
  const [editing, setEditing] = useState(false);
  // Surface a "mailbox full" warning here too: past quota, WildDuck silently
  // rejects inbound mail, so a "Receive email" step will just time out.
  const { data: mailboxQuota } = api.stablyEmail.getMailboxQuota.useQuery(
    { projectId: projectId! },
    { enabled: !!projectId && editing, retry: false },
  );

  const emailPrefix = useMemo(() => emailUsernameFromOrg(activeOrg?.orgName || ''), [activeOrg?.orgName]);

  type TestVariable = {
    name?: string;
    wrappedName?: string; // name surrounded by VARS['']
    value?: string;
    valueType?: 'STATIC' | 'DYNAMIC' | 'AI_EXTRACT_PROMPT';
  };

  const allAvailableInboxesVariables = useMemo(() => {
    const envInboxes: TestVariable[] =
      envVariables
        ?.filter((v) => typeof v.value === 'string' && v.value.endsWith(`@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}`))
        .map((v) => ({ name: v.name, wrappedName: `VARS['${v.name}']`, value: v.value, valueType: 'STATIC' })) || [];
    const testInboxes: TestVariable[] =
      testVariables
        .filter(({ value }) => {
          if (!value || typeof value !== 'string') {
            return false;
          }
          const { orgName, domain } = destructEmail(value);
          // Check if this is a valid Stably email inbox (matches org prefix and domain)
          // usernameSuffix can be empty for static inboxes like "org+@mail.stably.ai" which are
          // used when users want a fixed inbox address rather than a dynamically generated one
          return orgName === emailPrefix && domain === env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN;
        })
        .map((v) => ({ name: v.name, wrappedName: `VARS['${v.name}']`, value: v.value, valueType: v.valueType })) || [];

    // Combine all variables and deduplicate by name, prioritizing test variables over environment variables
    const allVariables = [...testInboxes, ...envInboxes];
    const seenNames = new Set<string | undefined>();
    const uniqueVariables = allVariables.filter((variable) => {
      if (seenNames.has(variable.name)) {
        return false;
      }
      seenNames.add(variable.name);
      return true;
    });

    return uniqueVariables;
  }, [testVariables, emailPrefix, envVariables]);

  const form = useForm<ReceiveEmailFormValues>({
    initialValues: {
      toEmailAddressVariable: toEmailAddressValue,
      toEmailAddressType,
      extractionType: variableExtraction?.extractionType || undefined,
      variableName: variableExtraction?.variableName || '',
      customExtractionPrompt: variableExtraction?.customExtractionPrompt || '',
      filters: filters || {
        sender: '',
        subject: '',
      },
    },
    validate: {
      variableName: (value, values) => {
        if (!values.extractionType || !values.extractionType) {
          return null;
        }
        const result = zVariableValidationType.safeParse(value);
        if (!result.success) {
          return result.error.message || 'Invalid variable name';
        }
        return null;
      },
      toEmailAddressVariable: (value, _values) => {
        if (value === '') {
          return 'Email inbox variable is required';
        }
        return null;
      },
      customExtractionPrompt: (value, values) => {
        if (values.extractionType === 'custom-extraction' && value === '') {
          return 'Please enter a prompt for extracting the content';
        }
        return null;
      },
    },
    mode: 'controlled',
  });

  form.watch('toEmailAddressVariable', ({ value }) => {
    setIsInboxSelected(!!value);
  });

  const numberOfActiveFilters = useDeepCompareMemo(() => {
    return Object.values(filters || {}).filter((v) => v !== '').length;
  }, [filters]);

  // Auto-expand filters or extraction if they have a value
  const defaultOpenAccordionItems = useMemo(() => {
    return [numberOfActiveFilters > 0 ? 'filters' : '', variableExtraction?.extractionType ? 'extraction' : ''];
  }, [numberOfActiveFilters, variableExtraction?.extractionType]);

  // we don't use form.watch here since we want to run this on initial load too
  useEffect(() => {
    const matchedEmailType = allAvailableInboxesVariables
      .filter((x) => x.wrappedName === form.values.toEmailAddressVariable)
      .at(0)?.valueType;
    if (matchedEmailType && matchedEmailType !== form.values.toEmailAddressType) {
      form.setFieldValue('toEmailAddressType', matchedEmailType);
    }
  }, [form, form.values.toEmailAddressVariable, form.values.toEmailAddressType, allAvailableInboxesVariables]);

  const handleClosePopover = useCallback(() => {
    toggleEditing(false);
    setEditing(false);
  }, [toggleEditing, setEditing]);

  return (
    <div className="flex items-center space-x-1 px-1">
      <Popover
        width={365}
        position="bottom"
        withArrow
        shadow="md"
        opened={editing}
        closeOnClickOutside
        onClose={handleClosePopover}
        onDismiss={handleClosePopover}
        middlewares={{
          // Popover height will have a max size of the available height. Prevents it from overflowing the viewport
          // https://floating-ui.com/docs/size
          size: {
            apply({ availableHeight, elements }) {
              Object.assign(elements.floating.style, {
                maxHeight: `${availableHeight}px`,
              });
            },
            padding: 8,
          },
        }}
      >
        <Popover.Target>
          <div
            className="break rounded-md"
            onDoubleClick={() => {
              onClick?.();
              toggleEditing(true);
              setEditing(true);
            }}
          >
            <ReceiveEmailSummary
              variableExtraction={variableExtraction}
              toEmailAddressValue={toEmailAddressValue}
              filters={filters}
            />
          </div>
        </Popover.Target>
        <Popover.Dropdown
          className="flex flex-col pr-0 dark:bg-neutral-800"
          onClick={(e) => e.stopPropagation()}
        >
          <ScrollArea.Autosize
            offsetScrollbars
            scrollbarSize={8}
            classNames={{ content: 'pr-1' }}
          >
            <div className="flex items-center justify-between">
              {allAvailableInboxesVariables.length > 0 ?
                <div className="text-sm">Receive Email</div>
              : <div className="text-sm text-gray-600 dark:text-gray-400">No email inboxes added for this test</div>}
              <CloseButton
                variant="transparent"
                size="md"
                onClick={handleClosePopover}
              />
            </div>
            <MailboxQuotaAlert
              quota={mailboxQuota?.quota}
              className="mt-2"
            />
            {allAvailableInboxesVariables.length > 0 ?
              <form
                className="mb-2 flex flex-col gap-2"
                onSubmit={form.onSubmit(
                  async ({
                    extractionType,
                    variableName,
                    toEmailAddressVariable,
                    filters: filterValues,
                    customExtractionPrompt,
                  }) => {
                    toggleEditing(false);
                    setEditing(false);
                    await updateFunc({
                      // we always reference the inbox by the variable name itself to ensure they are linked properly
                      toEmailAddressType: 'DYNAMIC',
                      toEmailAddressValue: toEmailAddressVariable,
                      variableExtraction:
                        extractionType ? { extractionType, variableName, customExtractionPrompt } : undefined,
                      filters: filterValues,
                    });
                  },
                )}
                ref={animationParent}
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <Select
                      label="Email Inbox"
                      defaultValue={allAvailableInboxesVariables.at(0)?.value}
                      data={allAvailableInboxesVariables.map((v) => ({
                        value: v.wrappedName ?? '',
                        label: v.name ?? '',
                      }))}
                      allowDeselect={false}
                      comboboxProps={{ withinPortal: false }}
                      {...form.getInputProps('toEmailAddressVariable')}
                    />
                    {form.values.toEmailAddressType === 'STATIC' && (
                      <div className="mt-1 flex justify-end">
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          leftSection={<IconCopy size={12} />}
                          disabled={!isInboxSelected}
                          color={clipboard.copied ? 'teal' : 'blue'}
                          onClick={() => {
                            const emailAddress = allAvailableInboxesVariables.find(
                              (v) => v.wrappedName === form.values.toEmailAddressVariable,
                            )?.value;
                            if (!emailAddress) {
                              notifications.show({
                                message: 'Failed to copy email address',
                                color: 'red',
                              });
                              return;
                            }
                            clipboard.copy(emailAddress);
                            notifications.show({
                              message: 'Email address copied',
                              color: 'teal',
                            });
                          }}
                        >
                          Copy email address
                        </Button>
                      </div>
                    )}
                  </div>

                  <ReceiveEmailFields
                    form={form}
                    readonly={readonly}
                    numberOfActiveFilters={numberOfActiveFilters}
                    defaultOpenAccordionItems={defaultOpenAccordionItems}
                  />
                </div>

                {!readonly && (
                  <div className="flex justify-end space-x-1 py-4">
                    <Button
                      type="submit"
                      disabled={readonly}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </form>
            : <div className="mt-4">
                <div className="text-sm font-medium">
                  Get an inbox by inserting a step above of the type{' '}
                  <span className="text-yellow-900 dark:text-yellow-600">Setup Email Inbox</span>
                </div>
              </div>
            }
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
